import { useState, useRef, DragEvent, ChangeEvent } from "react";
import { useEscKey } from "src/hooks/useEscKey";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TbX } from "react-icons/tb";
import { LuFiles } from "react-icons/lu";
import Toast, { type ToastState } from "src/components/Toast/Toast";
import { config } from "configs/config";
import { invalidateForMutation } from "src/services/apiCache";
import { isDemoMode } from "src/demo/demoApi";
import { xhrRequest } from "src/services/padUploadService";
import { useInvoiceOwnerChoice } from "src/hooks/useInvoiceOwnerChoice";
import type { Tag } from "src/types";
import "src/components/Popup/popup.scss";
import "./invoices.scss";

interface SendFailure {
    readableId:    string;
    recipientName: string;
    reason:        string;
}

interface DuplicateUpload {
    uploadedByName: string | null;
    uploadedAt:     string;
    sameDoctor:     boolean;
}

interface PossibleDuplicate {
    index:              number;
    patientName:        string;
    existingReadableId: string;
}

interface UploadSummary {
    invoices:    number;
    recipients:  number;
    patients:    number;
    samePersonInvoices?: number;
    totalAmount: number;
    recipientsMissingEmail?: number;
    emailsSent?:   number;
    emailsFailed?: number;
    sendFailures?: SendFailure[];
    duplicateUpload?: DuplicateUpload | null;
    /** Invoices skipped because the same patient already has an invoice with the exact same
     *  billed services/amount — a different file (re-export, different filename) describing
     *  what looks like the same real invoice, not a new one. Never silently created; always
     *  reported here so staff can check server/InvoicePage. */
    possibleDuplicates?: PossibleDuplicate[];
}

// Existing invoice duplicate conflict (from /check)
interface InvoiceConflict {
    index:         number;
    recipientName: string;
    patientName:   string;
    creationDate:  string;
    amount:        number;
}

// Recipient name ambiguity conflict (from /preview-recipients)
interface RecipientCandidate {
    id: string;        // real recipientId or "batch:N"
    firstName: string;
    lastName: string;
    street: string;
    plz: string;
    city: string;
}

interface RecipientConflict {
    blockIndex: number;
    padRecipient: { firstName: string; lastName: string; street: string; plz: string; city: string };
    candidates: RecipientCandidate[];
}

interface QueuedFile {
    file: File;
    id: string;
    status: "pending" | "uploading" | "done" | "error";
    progress: number;
    phase?: "reading" | "parsing" | "storing";
    summary?: UploadSummary;
    conflicts?: InvoiceConflict[];
}

const fmtEUR = (n: number) =>
    n.toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function PadUploadPopup({
    onClose,
    onSuccess,
    tags,
}: {
    onClose: () => void;
    /** Called once after a successful upload (any file). `sendFailureCount` — when
     *  greater than 0 — lets the caller show a failure-aware toast instead of the
     *  plain success one; this component doesn't fire its own success toast itself
     *  (it reuses its local `toast` state only for upload-level errors below), so
     *  there's never two toasts racing for the same fixed screen position. */
    onSuccess: (sendFailureCount?: number) => void;
    /** Admin-only, Plus+ feature — omitted entirely when tags aren't available. */
    tags?: Tag[];
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    useEscKey(onClose, true);
    const [files, setFiles]         = useState<QueuedFile[]>([]);
    const [dragging, setDragging]   = useState(false);
    const [uploading, setUploading] = useState(false);
    const [checking, setChecking]   = useState(false);
    const [toast, setToast]         = useState<ToastState | null>(null);
    const [results, setResults]     = useState<UploadSummary | null>(null);
    const [selectedTagId, setSelectedTagId] = useState("");

    // Multi-doctor praxis: which doctor this batch belongs to (backend/uploadController.ts's
    // applyExplicitDoctorPick). Irrelevant with only one doctor in the praxis, so the control
    // only renders when there's an actual choice to make. Defaults to the uploader's own id when
    // they themselves are one of the doctors (the common case — a doctor uploading their own
    // invoices shouldn't have to pick themselves); left unset otherwise (e.g. a MedicalAssistant
    // has no "own" doctor identity to default to), forcing an explicit choice.
    // The rule itself (doctors/admins upload for themselves, staff pick) lives in
    // useInvoiceOwnerChoice — shared with manual invoice creation.
    const [selectedDoctorId, setSelectedDoctorId] = useState("");
    const {
        doctors,
        showPicker: showDoctorPicker,
        effectiveDoctorId,
        // Staff never upload for themselves: with several doctors the server refuses an upload
        // without a pick, so the button waits for one. (With a single doctor the server assigns that one.)
        missing: doctorMissing,
    } = useInvoiceOwnerChoice(selectedDoctorId);

    // Invoice duplicate conflicts (existing behaviour)
    const [pendingConflicts, setPendingConflicts] = useState<{ qf: QueuedFile; conflicts: InvoiceConflict[] }[]>([]);
    // Per-conflict "replace" choice, keyed by `${fileId}:${blockIndex}` — defaults to checked
    // (replace) for every conflict so the button's default behavior matches the old all-replace
    // flow; unchecking a row leaves that specific existing invoice untouched on import.
    const [conflictReplace, setConflictReplace] = useState<Record<string, boolean>>({});

    // Recipient name ambiguity conflicts — doctor must resolve each before import
    const [recipientConflicts, setRecipientConflicts] = useState<RecipientConflict[]>([]);
    const [recipientResolutions, setRecipientResolutions] = useState<Record<number, string | null>>({});
    const [pendingRecipientFiles, setPendingRecipientFiles] = useState<QueuedFile[]>([]);

    // Same file content already uploaded before (by anyone in the praxis) — must be
    // confirmed before import, unlike the other two conflict types above this only ever
    // gates files that ARE duplicates; a normal, never-seen-before file skips this
    // entirely and goes straight into the existing flow below, unaffected.
    const [pendingDuplicates, setPendingDuplicates] = useState<{ qf: QueuedFile; duplicateUpload: DuplicateUpload; conflicts: InvoiceConflict[] }[]>([]);

    // Semantic duplicates — same patient, same billed services/amount as an invoice already on
    // file, but under a different (never-seen-before) file, so the whole-file check above never
    // catches it. Preflight preview from /check (uploadController.ts's checkPadFile), same idea
    // as the invoice-conflicts review above, just for content instead of an exact invoiceId
    // match. Kept per-file (not merged into pendingConflicts) since the resolution is opposite:
    // unchecked here means "skip" (the safe default), whereas an invoice conflict defaults to
    // checked ("replace"). possibleDuplicatesByFile carries every file's preview result forward
    // through the other gates below, since a file can clear the whole-file/invoice-conflict gates
    // and still have semantic duplicates waiting to be resolved before it's actually uploaded.
    const [pendingSemanticDuplicates, setPendingSemanticDuplicates] = useState<{ qf: QueuedFile; duplicates: PossibleDuplicate[] }[]>([]);
    const [forceChecked, setForceChecked] = useState<Record<string, boolean>>({});
    const [possibleDuplicatesByFile, setPossibleDuplicatesByFile] = useState<Record<string, PossibleDuplicate[]>>({});

    const inputRef = useRef<HTMLInputElement>(null);

    const addFiles = (list: FileList | null) => {
        if (!list) return;
        setFiles((prev) => [
            ...prev,
            ...Array.from(list).map((file) => ({
                file,
                id: `${file.name}-${Date.now()}-${Math.random()}`,
                status: "pending" as const,
                progress: 0,
            })),
        ]);
    };

    const handleDrop      = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); };
    const handleDragOver  = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragging(true); };
    const handleDragLeave = () => setDragging(false);
    const handleBrowse    = (e: ChangeEvent<HTMLInputElement>) => { addFiles(e.target.files); e.target.value = ""; };

    const doUpload = async (
        filesToUpload: QueuedFile[],
        resolutions?: Record<number, string | null>,
        skipIndicesByFile?: Record<string, number[]>,
        forceIndicesByFile?: Record<string, number[]>,
    ) => {
        setUploading(true);
        let anySuccess = false;
        const accumulated: UploadSummary = {
            invoices: 0, recipients: 0, patients: 0, samePersonInvoices: 0, totalAmount: 0, recipientsMissingEmail: 0,
            emailsSent: 0, emailsFailed: 0, sendFailures: [], duplicateUpload: null, possibleDuplicates: [],
        };

        for (const qf of filesToUpload) {
            setFiles((p) => p.map((f) => f.id === qf.id ? { ...f, status: "uploading", progress: 0 } : f));
            try {
                const extraFields: Record<string, string> = {};
                if (resolutions && Object.keys(resolutions).length > 0) {
                    extraFields.resolutions = JSON.stringify(resolutions);
                }
                const skipIndices = skipIndicesByFile?.[qf.id];
                if (skipIndices && skipIndices.length > 0) {
                    extraFields.skipIndices = JSON.stringify(skipIndices);
                }
                const forceIndices = forceIndicesByFile?.[qf.id];
                if (forceIndices && forceIndices.length > 0) {
                    extraFields.forceIndices = JSON.stringify(forceIndices);
                }
                if (selectedTagId) extraFields.tagId = selectedTagId;
                if (showDoctorPicker && effectiveDoctorId) extraFields.doctorId = effectiveDoctorId;

                const res = await xhrRequest(
                    `${config.baseUrl}/api/upload`,
                    qf.file,
                    extraFields,
                    (pct) => {
                        setFiles((p) => p.map((f) => f.id === qf.id ? { ...f, progress: pct, phase: "reading" } : f));
                    },
                    () => {
                        setFiles((p) => p.map((f) => f.id === qf.id ? { ...f, progress: 100, phase: "parsing" } : f));
                    },
                );

                if (res.error) {
                    setFiles((p) => p.map((f) => f.id === qf.id ? { ...f, status: "error", progress: 0, phase: undefined } : f));
                    setToast({ message: `${t("fileUpload.uploadFailed")} ${res.error}`, variant: "error" });
                } else {
                    const s: UploadSummary = { invoices: 0, recipients: 0, patients: 0, samePersonInvoices: 0, totalAmount: 0, recipientsMissingEmail: 0, ...res.summary };
                    setFiles((p) => p.map((f) => f.id === qf.id ? { ...f, status: "done", progress: 100, phase: "storing", summary: s } : f));
                    accumulated.invoices    += s.invoices;
                    accumulated.recipients  += s.recipients;
                    accumulated.patients    += s.patients;
                    accumulated.samePersonInvoices = (accumulated.samePersonInvoices ?? 0) + (s.samePersonInvoices ?? 0);
                    accumulated.totalAmount  = +(accumulated.totalAmount + s.totalAmount).toFixed(2);
                    accumulated.recipientsMissingEmail = (accumulated.recipientsMissingEmail ?? 0) + (s.recipientsMissingEmail ?? 0);
                    accumulated.emailsSent   = (accumulated.emailsSent   ?? 0) + (s.emailsSent   ?? 0);
                    accumulated.emailsFailed = (accumulated.emailsFailed ?? 0) + (s.emailsFailed ?? 0);
                    accumulated.sendFailures = [...(accumulated.sendFailures ?? []), ...(s.sendFailures ?? [])];
                    accumulated.possibleDuplicates = [...(accumulated.possibleDuplicates ?? []), ...(s.possibleDuplicates ?? [])];
                    if (s.duplicateUpload) accumulated.duplicateUpload = s.duplicateUpload;
                    invalidateForMutation("/api/upload");
                    fetch(`${config.baseUrl}/api/upload/migrate-pad-source`, {
                        method: "POST",
                        headers: {
                            ...(localStorage.getItem("token") && { Authorization: `Bearer ${localStorage.getItem("token")}` }),
                            ...(JSON.parse(localStorage.getItem("doctor") || "null")?.id && { "X-Doctor-Id": JSON.parse(localStorage.getItem("doctor") || "null").id }),
                            ...(JSON.parse(localStorage.getItem("praxis") || "null")?.id && { "X-Praxis-Id": JSON.parse(localStorage.getItem("praxis") || "null").id }),
                        },
                    }).catch(() => {});
                    anySuccess = true;
                }
            } catch (err: any) {
                setFiles((p) => p.map((f) => f.id === qf.id ? { ...f, status: "error", progress: 0 } : f));
                setToast({ message: `${t("fileUpload.uploadFailed")} ${err?.message ?? "Unknown error"}`, variant: "error" });
            }
        }

        setUploading(false);
        if (anySuccess) {
            onSuccess(accumulated.sendFailures?.length ?? 0);
            setResults(accumulated);
        }
    };

    // Routes files that have cleared the whole-file and invoice-conflict gates into the actual
    // upload — except any that still have unresolved semantic duplicates (from the /check preview
    // that ran back in handleUpload), which get one more gate first. `dupMap` is passed explicitly
    // rather than read from possibleDuplicatesByFile state so handleUpload's own call (same tick
    // the state is set) never risks reading a stale closure.
    const routeToUpload = async (
        qfs: QueuedFile[],
        dupMap: Record<string, PossibleDuplicate[]>,
        skipIndicesByFile?: Record<string, number[]>,
    ) => {
        const withSemanticDuplicates = qfs.filter((qf) => (dupMap[qf.id]?.length ?? 0) > 0);
        const clean = qfs.filter((qf) => !(dupMap[qf.id]?.length > 0));

        if (withSemanticDuplicates.length > 0) {
            setPendingSemanticDuplicates((prev) => [
                ...prev,
                ...withSemanticDuplicates.map((qf) => ({ qf, duplicates: dupMap[qf.id] })),
            ]);
        }
        if (clean.length > 0) await doUpload(clean, undefined, skipIndicesByFile);
    };

    const handleUpload = async () => {
        if (isDemoMode()) {
            setToast({ message: t("fileUpload.demoDisabled") ?? "Upload is disabled in demo mode.", variant: "success" });
            return;
        }
        const pending = files.filter((f) => f.status === "pending");
        if (!pending.length) return;

        setChecking(true);
        const withDuplicates: { qf: QueuedFile; duplicateUpload: DuplicateUpload; conflicts: InvoiceConflict[] }[] = [];
        const withInvoiceConflicts: { qf: QueuedFile; conflicts: InvoiceConflict[] }[] = [];
        const dupMap: Record<string, PossibleDuplicate[]> = {};
        let clean: QueuedFile[] = [];

        // Step 1: check for invoice duplicates, whole-file re-uploads, AND semantic duplicates
        // (same patient/services/amount under a never-seen-before file), all in the same /check
        // call that already ran here before that last one was added — a file with none of the
        // three takes the exact same path (straight into `clean`) as always.
        const checkFields: Record<string, string> =
            showDoctorPicker && effectiveDoctorId ? { doctorId: effectiveDoctorId } : {};
        for (const qf of pending) {
            try {
                const res = await xhrRequest(`${config.baseUrl}/api/upload/check`, qf.file, checkFields);
                if (res.possibleDuplicates?.length > 0) dupMap[qf.id] = res.possibleDuplicates;
                if (res.duplicateUpload) {
                    withDuplicates.push({ qf, duplicateUpload: res.duplicateUpload, conflicts: res.conflicts ?? [] });
                } else if (res.conflicts?.length > 0) {
                    withInvoiceConflicts.push({ qf, conflicts: res.conflicts });
                } else {
                    clean.push(qf);
                }
            } catch {
                clean.push(qf);
            }
        }

        setChecking(false);
        if (Object.keys(dupMap).length > 0) setPossibleDuplicatesByFile((prev) => ({ ...prev, ...dupMap }));

        // Whole-file duplicates are confirmed first — only once the doctor says "upload
        // anyway" do those files fall through to the normal invoice-conflict review below.
        if (withDuplicates.length > 0) {
            setPendingDuplicates(withDuplicates);
        }

        if (withInvoiceConflicts.length > 0) {
            setPendingConflicts(withInvoiceConflicts);
            // Default every conflict to "replace" — matches the previous all-replace behavior
            // when the doctor doesn't touch anything, only unchecking a row opts it out.
            const initial: Record<string, boolean> = {};
            for (const { qf, conflicts } of withInvoiceConflicts) {
                for (const c of conflicts) initial[`${qf.id}:${c.index}`] = true;
            }
            setConflictReplace((prev) => ({ ...prev, ...initial }));
        }

        if (clean.length > 0) await routeToUpload(clean, dupMap);
    };

    const handleConfirmDuplicates = async () => {
        const confirmed = pendingDuplicates;
        setPendingDuplicates([]);

        const stillNeedsReview = confirmed.filter(({ conflicts }) => conflicts.length > 0);
        const readyToUpload    = confirmed.filter(({ conflicts }) => conflicts.length === 0).map(({ qf }) => qf);

        if (stillNeedsReview.length > 0) {
            const initial: Record<string, boolean> = {};
            for (const { qf, conflicts } of stillNeedsReview) {
                for (const c of conflicts) initial[`${qf.id}:${c.index}`] = true;
            }
            setConflictReplace((prev) => ({ ...prev, ...initial }));
            setPendingConflicts((prev) => [...prev, ...stillNeedsReview.map(({ qf, conflicts }) => ({ qf, conflicts }))]);
        }

        if (readyToUpload.length > 0) await routeToUpload(readyToUpload, possibleDuplicatesByFile);
    };

    const handleCancelDuplicates = () => {
        // Same convention as the other two conflict cancel-handlers — reset back to
        // "pending" rather than removing the file, so the Upload button still has
        // something to act on if the doctor changes their mind.
        const ids = new Set(pendingDuplicates.map((pd) => pd.qf.id));
        setFiles((p) => p.map((f) => ids.has(f.id) ? { ...f, status: "pending", progress: 0, phase: undefined } : f));
        setPendingDuplicates([]);
    };

    // Step 2: check for recipient name ambiguity
    const runRecipientPreview = async (filesToCheck: QueuedFile[]) => {
        const withRecipientConflicts: { qf: QueuedFile; conflicts: RecipientConflict[] }[] = [];
        const fullyClean: QueuedFile[] = [];

        for (const qf of filesToCheck) {
            try {
                const res = await xhrRequest(`${config.baseUrl}/api/upload/preview-recipients`, qf.file);
                if (res.conflicts?.length > 0) {
                    withRecipientConflicts.push({ qf, conflicts: res.conflicts });
                } else {
                    fullyClean.push(qf);
                }
            } catch {
                fullyClean.push(qf);
            }
        }

        setChecking(false);

        if (withRecipientConflicts.length > 0) {
            // Flatten all conflicts (typically one file at a time)
            setRecipientConflicts(withRecipientConflicts.flatMap((x) => x.conflicts));
            setRecipientResolutions({});
            setPendingRecipientFiles(withRecipientConflicts.map((x) => x.qf));
            if (fullyClean.length > 0) await doUpload(fullyClean);
        } else {
            await doUpload(fullyClean);
        }
    };

    const handleConfirmRecipientConflicts = async () => {
        const filesToUpload = pendingRecipientFiles;
        const resolutions   = recipientResolutions;
        setRecipientConflicts([]);
        setRecipientResolutions({});
        setPendingRecipientFiles([]);
        await doUpload(filesToUpload, resolutions);
    };

    const handleCancelRecipientConflicts = () => {
        // Reset back to "pending" rather than removing the file — otherwise there's nothing
        // left in the list to retry and the Upload button has no pending file to act on.
        const ids = new Set(pendingRecipientFiles.map((f) => f.id));
        setFiles((p) => p.map((f) => ids.has(f.id) ? { ...f, status: "pending", progress: 0, phase: undefined } : f));
        setRecipientConflicts([]);
        setRecipientResolutions({});
        setPendingRecipientFiles([]);
    };

    const toggleConflictReplace = (fileId: string, index: number) => {
        const key = `${fileId}:${index}`;
        setConflictReplace((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleConfirmReplace = async () => {
        const toUpload = pendingConflicts.map((pc) => pc.qf);
        // Unchecked conflicts become skipIndices for their file — that invoice, and only that
        // invoice, is left completely untouched (see createExams.ts/createInvoices.ts).
        const skipIndicesByFile: Record<string, number[]> = {};
        for (const { qf, conflicts } of pendingConflicts) {
            const skipped = conflicts.filter((c) => !conflictReplace[`${qf.id}:${c.index}`]).map((c) => c.index);
            if (skipped.length > 0) skipIndicesByFile[qf.id] = skipped;
        }
        setPendingConflicts([]);
        setConflictReplace({});
        await routeToUpload(toUpload, possibleDuplicatesByFile, skipIndicesByFile);
    };

    const handleCancelConflicts = () => {
        // Reset back to "pending" rather than removing the file — otherwise there's nothing
        // left in the list to retry and the Upload button has no pending file to act on.
        const conflictIds = new Set(pendingConflicts.map((pc) => pc.qf.id));
        setFiles((p) => p.map((f) => conflictIds.has(f.id) ? { ...f, status: "pending", progress: 0, phase: undefined } : f));
        setPendingConflicts([]);
        setConflictReplace({});
    };

    const toggleForceChecked = (fileId: string, index: number) => {
        const key = `${fileId}:${index}`;
        setForceChecked((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleConfirmSemanticDuplicates = async () => {
        const toUpload = pendingSemanticDuplicates.map((p) => p.qf);
        // Checked rows become forceIndices for their file — everything else stays skipped
        // (uploadPadFile re-runs this same check and auto-skips anything not forced).
        const forceIndicesByFile: Record<string, number[]> = {};
        for (const { qf, duplicates } of pendingSemanticDuplicates) {
            const forced = duplicates.filter((d) => forceChecked[`${qf.id}:${d.index}`]).map((d) => d.index);
            if (forced.length > 0) forceIndicesByFile[qf.id] = forced;
        }
        setPendingSemanticDuplicates([]);
        setForceChecked({});
        await doUpload(toUpload, undefined, undefined, forceIndicesByFile);
    };

    const handleCancelSemanticDuplicates = () => {
        // Same convention as the other conflict cancel-handlers — reset back to "pending"
        // rather than removing the file, so the Upload button still has something to act on.
        const ids = new Set(pendingSemanticDuplicates.map((p) => p.qf.id));
        setFiles((p) => p.map((f) => ids.has(f.id) ? { ...f, status: "pending", progress: 0, phase: undefined } : f));
        setPendingSemanticDuplicates([]);
        setForceChecked({});
    };

    const resolveRecipient = (blockIndex: number, recipientId: string | null) => {
        setRecipientResolutions((prev) => ({ ...prev, [blockIndex]: recipientId }));
    };

    const allResolved = recipientConflicts.length > 0 &&
        recipientConflicts.every((c) => recipientResolutions[c.blockIndex] !== undefined);

    const hasPending = files.some((f) => f.status === "pending");
    const demoDisabled = isDemoMode();
    const showDuplicates = pendingDuplicates.length > 0;
    const showInvoiceConflicts  = pendingConflicts.length > 0;
    const showRecipientConflicts = recipientConflicts.length > 0;
    const showSemanticDuplicates = pendingSemanticDuplicates.length > 0;
    const replaceSelectedCount = pendingConflicts.reduce(
        (sum, { qf, conflicts }) => sum + conflicts.filter((c) => conflictReplace[`${qf.id}:${c.index}`]).length,
        0,
    );
    const forceSelectedCount = pendingSemanticDuplicates.reduce(
        (sum, { qf, duplicates }) => sum + duplicates.filter((d) => forceChecked[`${qf.id}:${d.index}`]).length,
        0,
    );

    return (
        <>
            {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}

            <div className="popup-overlay" onClick={onClose}>
                <div className="popup pad-upload-popup" onClick={(e) => e.stopPropagation()}>

                    <div className="popup__header">
                        <span className="popup__title">{t("fileUpload.uploadTitle")}</span>
                        <button className="popup__close" onClick={onClose}><TbX /></button>
                    </div>

                    {showDoctorPicker && !results && (
                        <div className="pad-upload-tag">
                            <label htmlFor="pad-upload-doctor-select">{t("fileUpload.uploadForDoctorLabel")}</label>
                            <select
                                id="pad-upload-doctor-select"
                                value={effectiveDoctorId}
                                onChange={(e) => setSelectedDoctorId(e.target.value)}
                            >
                                <option value="">{t("fileUpload.uploadForDoctorPlaceholder")}</option>
                                {doctors.map((doc) => (
                                    <option key={doc.id} value={doc.id}>
                                        {[doc.salutation, doc.title, doc.firstName, doc.lastName].filter(Boolean).join(" ")}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {!!tags?.length && !results && (
                        <div className="pad-upload-tag">
                            <label htmlFor="pad-upload-tag-select">{t("tags.tagThisBatch")}</label>
                            <select
                                id="pad-upload-tag-select"
                                value={selectedTagId}
                                onChange={(e) => setSelectedTagId(e.target.value)}
                            >
                                <option value="">{t("tags.noTag")}</option>
                                {tags.map((tag) => (
                                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div
                        className={`pad-upload-drop${dragging ? " pad-upload-drop--over" : ""}`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => inputRef.current?.click()}
                    >
                        <span className="pad-upload-drop__text">
                            {t("fileUpload.dropFiles")}{" "}
                            <span className="pad-upload-drop__browse"
                                onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}>
                                {t("fileUpload.browse")}
                            </span>
                        </span>
                        <span className="pad-upload-drop__types">{t("fileUpload.uploadAcceptedTypes")}</span>
                        <input
                            ref={inputRef}
                            type="file"
                            multiple
                            className="pad-upload-input"
                            onChange={handleBrowse}
                        />
                    </div>

                    <div className="pad-upload-files">
                        <span className="pad-upload-files__label">{t("fileUpload.uploadedFiles")}</span>

                        {files.length === 0 ? (
                            <div className="pad-upload-empty">
                                <LuFiles className="pad-upload-empty__icon" />
                                <span className="pad-upload-empty__text">{t("fileUpload.filesWillAppear")}</span>
                            </div>
                        ) : (
                            <div className="pad-upload-list">
                                {files.map((f) => (
                                    <div key={f.id} className={`pad-upload-item pad-upload-item--${f.status}`}>
                                        <div className="pad-upload-item__top">
                                            <span className="pad-upload-item__name">{f.file.name}</span>
                                            <div className="pad-upload-item__actions">
                                                {f.status === "done"    && <span className="pad-upload-item__done">✓</span>}
                                                {f.status === "error"   && <span className="pad-upload-item__err">✕</span>}
                                                {f.status === "uploading" && f.phase !== "parsing" && (
                                                    <span className="pad-upload-item__pct">{f.progress}%</span>
                                                )}
                                                {f.status === "uploading" && f.phase === "parsing" && (
                                                    <span className="pad-upload-item__pct pad-upload-item__pct--parsing">⋯</span>
                                                )}
                                                {f.status === "pending" && (
                                                    <button
                                                        className="pad-upload-item__remove"
                                                        onClick={(e) => { e.stopPropagation(); setFiles((p) => p.filter((x) => x.id !== f.id)); }}
                                                    >
                                                        <TbX />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {f.status === "uploading" && (
                                            <div className="pad-upload-item__track">
                                                <div
                                                    className={`pad-upload-item__bar${f.phase === "parsing" ? " pad-upload-item__bar--parsing" : ""}`}
                                                    style={f.phase !== "parsing" ? { ["--w" as any]: `${f.progress}%` } : undefined}
                                                />
                                            </div>
                                        )}
                                        {f.status === "uploading" && f.phase && (
                                            <span className="pad-upload-item__phase">
                                                {t(`fileUpload.phase${f.phase.charAt(0).toUpperCase() + f.phase.slice(1)}`)}
                                            </span>
                                        )}
                                        {f.status === "done" && (
                                            <span className="pad-upload-item__phase pad-upload-item__phase--success">
                                                {t("fileUpload.phaseComplete")}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Whole-file re-upload confirmation */}
                    {showDuplicates && (
                        <div className="pad-upload-conflicts">
                            <div className="pad-upload-conflicts__title">{t("fileUpload.duplicateUploadConfirmTitle")}</div>
                            <div className="pad-upload-conflicts__subtitle">{t("fileUpload.duplicateUploadConfirmSubtitle")}</div>
                            {pendingDuplicates.map(({ qf, duplicateUpload }) => (
                                <p key={qf.id} className="pad-upload-summary__duplicate-warning">
                                    <strong>{qf.file.name}</strong>
                                    {": "}
                                    {t(
                                        duplicateUpload.sameDoctor
                                            ? "fileUpload.duplicateUploadSame"
                                            : "fileUpload.duplicateUploadOther",
                                        {
                                            name: duplicateUpload.uploadedByName ?? t("fileUpload.duplicateUploadUnknownDoctor"),
                                            date: new Date(duplicateUpload.uploadedAt).toLocaleDateString("de-DE"),
                                        },
                                    )}
                                </p>
                            ))}
                        </div>
                    )}

                    {/* Invoice duplicate conflicts */}
                    {showInvoiceConflicts && (
                        <div className="pad-upload-conflicts">
                            <div className="pad-upload-conflicts__title">{t("fileUpload.conflictsTitle")}</div>
                            <div className="pad-upload-conflicts__subtitle">{t("fileUpload.conflictsSubtitle")}</div>
                            {pendingConflicts.flatMap(({ qf, conflicts }) =>
                                conflicts.map((c) => {
                                    const key = `${qf.id}:${c.index}`;
                                    return (
                                        <label key={key} className="pad-upload-conflict-row">
                                            <input
                                                type="checkbox"
                                                checked={!!conflictReplace[key]}
                                                onChange={() => toggleConflictReplace(qf.id, c.index)}
                                            />
                                            <div className="pad-upload-conflict-row__body">
                                                <div className="pad-upload-conflict-row__names">
                                                    <span className="pad-upload-conflict-row__label">{t("fileUpload.conflictRecipientLabel")}</span>
                                                    <span className="pad-upload-conflict-row__value">{c.recipientName}</span>
                                                    {c.patientName !== c.recipientName && (
                                                        <>
                                                            <span className="pad-upload-conflict-row__label">{t("fileUpload.conflictPatientLabel")}</span>
                                                            <span className="pad-upload-conflict-row__value">{c.patientName}</span>
                                                        </>
                                                    )}
                                                </div>
                                                <div className="pad-upload-conflict-row__meta">
                                                    <span className="pad-upload-conflict-row__meta-item">
                                                        {t("fileUpload.conflictDateLabel")} <strong>{new Date(c.creationDate).toLocaleDateString("de-DE")}</strong>
                                                    </span>
                                                    <span className="pad-upload-conflict-row__meta-item">
                                                        {t("fileUpload.conflictAmountLabel")} <strong>{fmtEUR(c.amount)}</strong>
                                                    </span>
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })
                            )}
                        </div>
                    )}

                    {/* Semantic duplicates — same patient/services/amount under a different file */}
                    {showSemanticDuplicates && (
                        <div className="pad-upload-conflicts">
                            <div className="pad-upload-conflicts__title">{t("fileUpload.semanticDuplicatesTitle")}</div>
                            <div className="pad-upload-conflicts__subtitle">{t("fileUpload.semanticDuplicatesSubtitle")}</div>
                            {pendingSemanticDuplicates.flatMap(({ qf, duplicates }) =>
                                duplicates.map((d) => {
                                    const key = `${qf.id}:${d.index}`;
                                    return (
                                        <label key={key} className="pad-upload-conflict-row">
                                            <input
                                                type="checkbox"
                                                checked={!!forceChecked[key]}
                                                onChange={() => toggleForceChecked(qf.id, d.index)}
                                            />
                                            <div className="pad-upload-conflict-row__body">
                                                <div className="pad-upload-conflict-row__names">
                                                    <span className="pad-upload-conflict-row__label">{t("fileUpload.conflictPatientLabel")}</span>
                                                    <span className="pad-upload-conflict-row__value">{d.patientName}</span>
                                                </div>
                                                <div className="pad-upload-conflict-row__meta">
                                                    <span className="pad-upload-conflict-row__meta-item">
                                                        {t("fileUpload.semanticDuplicateExistingLabel")} <strong>{d.existingReadableId}</strong>
                                                    </span>
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })
                            )}
                        </div>
                    )}

                    {/* Recipient name ambiguity conflicts */}
                    {showRecipientConflicts && (
                        <div className="pad-recipient-conflicts">
                            <div className="pad-recipient-conflicts__header">
                                <span className="pad-recipient-conflicts__title">
                                    {t("fileUpload.recipientConflictsTitle")}
                                </span>
                                {recipientConflicts.length > 1 && (
                                    <span className="pad-recipient-conflicts__count">
                                        {Object.keys(recipientResolutions).length}/{recipientConflicts.length}
                                    </span>
                                )}
                            </div>
                            <div className="pad-recipient-conflicts__list">
                            {recipientConflicts.map((c) => (
                                <div key={c.blockIndex} className={`pad-recipient-conflict-card${recipientResolutions[c.blockIndex] !== undefined ? " pad-recipient-conflict-card--resolved" : ""}`}>
                                    <div className="pad-recipient-conflict-card__sides">
                                        <div className="pad-recipient-conflict-card__side">
                                            <span className="pad-recipient-conflict-card__label">{t("fileUpload.recipientConflictFilePad")}</span>
                                            <span className="pad-recipient-conflict-card__name">
                                                {c.padRecipient.firstName} {c.padRecipient.lastName}
                                            </span>
                                            {c.padRecipient.street && (
                                                <span className="pad-recipient-conflict-card__addr">
                                                    {c.padRecipient.street}{c.padRecipient.plz ? `, ${c.padRecipient.plz}` : ""}{c.padRecipient.city ? ` ${c.padRecipient.city}` : ""}
                                                </span>
                                            )}
                                        </div>
                                        <div className="pad-recipient-conflict-card__divider" />
                                        <div className="pad-recipient-conflict-card__side">
                                            <span className="pad-recipient-conflict-card__label">{t("fileUpload.recipientConflictExisting")}</span>
                                            {c.candidates.map((cand) => (
                                                <div key={cand.id}>
                                                    <span className="pad-recipient-conflict-card__name">
                                                        {cand.firstName} {cand.lastName}
                                                    </span>
                                                    {cand.street && (
                                                        <span className="pad-recipient-conflict-card__addr">
                                                            {cand.street}{cand.plz ? `, ${cand.plz}` : ""}{cand.city ? ` ${cand.city}` : ""}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="pad-recipient-conflict-card__actions">
                                        <button
                                            className={`pad-recipient-conflict-card__btn pad-recipient-conflict-card__btn--merge${recipientResolutions[c.blockIndex] !== null && recipientResolutions[c.blockIndex] !== undefined ? " pad-recipient-conflict-card__btn--active" : ""}`}
                                            onClick={() => resolveRecipient(c.blockIndex, c.candidates[0].id)}
                                        >
                                            {t("fileUpload.recipientConflictMerge")}
                                        </button>
                                        <button
                                            className={`pad-recipient-conflict-card__btn pad-recipient-conflict-card__btn--new${recipientResolutions[c.blockIndex] === null ? " pad-recipient-conflict-card__btn--active" : ""}`}
                                            onClick={() => resolveRecipient(c.blockIndex, null)}
                                        >
                                            {t("fileUpload.recipientConflictCreateNew")}
                                        </button>
                                    </div>
                                </div>
                            ))}
                            </div>
                        </div>
                    )}

                    {results && (
                        <div className="pad-upload-summary">
                            {results.duplicateUpload && (
                                <p className="pad-upload-summary__duplicate-warning">
                                    {t(
                                        results.duplicateUpload.sameDoctor
                                            ? "fileUpload.duplicateUploadSame"
                                            : "fileUpload.duplicateUploadOther",
                                        {
                                            name: results.duplicateUpload.uploadedByName ?? t("fileUpload.duplicateUploadUnknownDoctor"),
                                            date: new Date(results.duplicateUpload.uploadedAt).toLocaleDateString("de-DE"),
                                        },
                                    )}
                                </p>
                            )}
                            <div className="pad-upload-summary__stat">
                                <span className="pad-upload-summary__value">{results.invoices}</span>
                                <span className="pad-upload-summary__label">{t("fileUpload.summaryInvoices")}</span>
                            </div>
                            <div className="pad-upload-summary__stat">
                                <span className="pad-upload-summary__value">{results.recipients}</span>
                                <span className="pad-upload-summary__label">{t("fileUpload.summaryAccountPatients")}</span>
                            </div>
                            <div className="pad-upload-summary__stat">
                                <span className="pad-upload-summary__value">{results.patients}</span>
                                <span className="pad-upload-summary__label">{t("fileUpload.summaryLinkedPatients")}</span>
                            </div>
                            <div className="pad-upload-summary__stat pad-upload-summary__stat--amount">
                                <span className="pad-upload-summary__value">€{results.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span className="pad-upload-summary__label">{t("fileUpload.summaryTotal")}</span>
                            </div>
                            {(results.samePersonInvoices ?? 0) > 0 && (
                                <p className="pad-upload-summary__same-person">
                                    {t(
                                        results.samePersonInvoices === 1
                                            ? "fileUpload.summarySamePerson_one"
                                            : "fileUpload.summarySamePerson_other",
                                        { count: results.samePersonInvoices },
                                    )}
                                </p>
                            )}
                            {(results.possibleDuplicates?.length ?? 0) > 0 && (
                                <div className="pad-upload-possible-duplicates" role="status">
                                    <p className="pad-upload-possible-duplicates__title">
                                        {t("fileUpload.possibleDuplicatesTitle", { count: results.possibleDuplicates!.length })}
                                    </p>
                                    <ul className="pad-upload-possible-duplicates__list">
                                        {results.possibleDuplicates!.map((d, i) => (
                                            <li key={`${d.existingReadableId}-${i}`} className="pad-upload-possible-duplicates__item">
                                                <span className="pad-upload-possible-duplicates__name">{d.patientName}</span>
                                                <span className="pad-upload-possible-duplicates__id">{d.existingReadableId}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {((results.emailsSent ?? 0) > 0 || (results.emailsFailed ?? 0) > 0) && (
                                <div className="pad-upload-summary__email-row">
                                    {(results.emailsSent ?? 0) > 0 && (
                                        <span className="pad-upload-summary__email-chip pad-upload-summary__email-chip--sent">
                                            {t("fileUpload.emailsSent", { count: results.emailsSent })}
                                        </span>
                                    )}
                                    {(results.emailsFailed ?? 0) > 0 && (
                                        <span className="pad-upload-summary__email-chip pad-upload-summary__email-chip--failed">
                                            {t("fileUpload.emailsFailed", { count: results.emailsFailed })}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {results && (results.sendFailures?.length ?? 0) > 0 && (
                        <div className="pad-upload-send-failures" role="status">
                            <div className="pad-upload-whats-next__title">{t("fileUpload.sendFailuresTitle")}</div>
                            <ul className="pad-upload-send-failures__list">
                                {results.sendFailures!.map((f, i) => (
                                    <li key={`${f.readableId}-${i}`} className="pad-upload-send-failures__item">
                                        <span className="pad-upload-send-failures__id">{f.readableId}</span>
                                        {f.recipientName && <span className="pad-upload-send-failures__name">{f.recipientName}</span>}
                                        <span className="pad-upload-send-failures__reason">
                                            {t(`fileUpload.sendFailureReason${f.reason}`, { defaultValue: t("fileUpload.sendFailureReasonUnknown") })}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {results && (results.recipientsMissingEmail ?? 0) > 0 && (
                        <div className="pad-upload-whats-next" role="status">
                            <div className="pad-upload-whats-next__title">{t("fileUpload.whatsNextTitle")}</div>
                            <p className="pad-upload-whats-next__text">{t("fileUpload.whatsNextMissingEmailBody")}</p>
                            <button
                                type="button"
                                className="pad-upload-whats-next__cta"
                                onClick={() => { onClose(); navigate("/patients"); }}
                            >
                                {t("fileUpload.whatsNextPatientsCta")}
                            </button>
                        </div>
                    )}

                    <div className="popup__actions">
                        {results ? (
                            <button className="popup__btn popup__btn--primary" onClick={onClose}>
                                {t("popup.done")}
                            </button>
                        ) : showDuplicates ? (
                            <>
                                <button className="popup__btn popup__btn--default" onClick={handleCancelDuplicates}>
                                    {t("popup.cancel")}
                                </button>
                                <button className="popup__btn popup__btn--primary" onClick={handleConfirmDuplicates}>
                                    {t("fileUpload.duplicateUploadConfirmUploadAnyway")}
                                </button>
                            </>
                        ) : showRecipientConflicts ? (
                            <>
                                <button className="popup__btn popup__btn--default" onClick={handleCancelRecipientConflicts}>
                                    {t("popup.cancel")}
                                </button>
                                <button
                                    className="popup__btn popup__btn--primary"
                                    onClick={handleConfirmRecipientConflicts}
                                    disabled={!allResolved}
                                >
                                    {t("fileUpload.recipientConflictsContinue")}
                                </button>
                            </>
                        ) : showInvoiceConflicts ? (
                            <>
                                <button className="popup__btn popup__btn--default" onClick={handleCancelConflicts}>
                                    {t("popup.cancel")}
                                </button>
                                <button className="popup__btn popup__btn--primary" onClick={handleConfirmReplace}>
                                    {replaceSelectedCount > 0
                                        ? `${t("fileUpload.conflictsReplace")} (${replaceSelectedCount})`
                                        : t("fileUpload.conflictsReplaceNone")}
                                </button>
                            </>
                        ) : showSemanticDuplicates ? (
                            <>
                                <button className="popup__btn popup__btn--default" onClick={handleCancelSemanticDuplicates}>
                                    {t("popup.cancel")}
                                </button>
                                <button className="popup__btn popup__btn--primary" onClick={handleConfirmSemanticDuplicates}>
                                    {forceSelectedCount > 0
                                        ? t("fileUpload.semanticDuplicatesImportSelected", { count: forceSelectedCount })
                                        : t("fileUpload.semanticDuplicatesSkipAll")}
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="popup__btn popup__btn--default" onClick={onClose}>
                                    {t("popup.cancel")}
                                </button>
                                <button
                                    className="popup__btn popup__btn--primary"
                                    onClick={handleUpload}
                                    disabled={demoDisabled || uploading || checking || !hasPending || doctorMissing}
                                >
                                    {checking ? t("fileUpload.checking") : uploading ? t("fileUpload.uploading") : t("fileUpload.uploadFile")}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
